// client/src/components/pages/TelegramBI/QueryBuilder/index.tsx (converted from index.js, ST-00259)
//
// ST-00259: toasts now render (AppToaster), and the list reloads on every keystroke in the
// search box and twice when a filter change also resets the page, so the "Failed to load
// queries" toast carries a stable id: a failing endpoint shows it once, not once per request.
import { useState, useEffect, useCallback, type ComponentType } from 'react';
import {
  FaPlay,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCopy,
  FaHistory,
  FaDatabase,
  FaSearch,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaRobot,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { PageHeader, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import type { HttpErrorLike } from '../../../../types/clientShell';
import type {
  TelegramEnvelope,
  TelegramQueryExecution,
  TelegramQueryForm,
  TelegramQueryListPage,
  TelegramQueryListParams,
  TelegramQueryResults,
  TelegramQueryRow,
  TelegramVisibilityFilter,
} from '../../../../types/clientPages/telegramBI';
import QueryEditorJs from './QueryEditor';
import QueryResultsJs from './QueryResults';
import QueryHistoryJs from './QueryHistory';
import CreateQueryModalJs from './CreateQueryModal';
import EditQueryModalJs from './EditQueryModal';
import AiAssistModal from '../../../shared/AiAssistPanel';
import { formatRelativeTime } from '../../../../utils/dateUtils';

/** The list toast's id: one "Failed to load queries" on screen however often the list reloads. */
export const QUERIES_LOAD_TOAST_ID = 'telegram-queries-load';

const PAGE_SIZE = 20;

// Typed views of the (still JavaScript) children, with the props this page passes.
const QueryEditor = QueryEditorJs as unknown as ComponentType<{
  query: TelegramQueryRow;
  readOnly?: boolean;
  onExecute?: () => void;
}>;
const QueryResults = QueryResultsJs as unknown as ComponentType<{
  results: TelegramQueryResults;
}>;
const QueryHistory = QueryHistoryJs as unknown as ComponentType<{
  queryId: number;
  onClose: () => void;
}>;
const CreateQueryModal = CreateQueryModalJs as unknown as ComponentType<{
  onClose: () => void;
  onSubmit: (data: TelegramQueryForm) => Promise<void>;
}>;
const EditQueryModal = EditQueryModalJs as unknown as ComponentType<{
  query: TelegramQueryRow;
  onClose: () => void;
  onSubmit: (data: TelegramQueryForm) => Promise<void>;
}>;

/** `{ success, data }` → data; a bare payload is passed through. */
function payloadOf<T>(body: unknown): T | undefined {
  return (body as TelegramEnvelope<T> | undefined)?.data || (body as T | undefined);
}

/** The server's words for a failed request (`error`, then `message`). */
const apiError = (err: unknown) => (err as HttpErrorLike | null | undefined)?.response?.data;

const QueryBuilder = () => {
  const { hasPermission } = useAuth();
  const showAiAssist = hasPermission('ai.assist');
  const [queries, setQueries] = useState<TelegramQueryRow[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<TelegramQueryRow | null>(null);
  const [queryResults, setQueryResults] = useState<TelegramQueryResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<TelegramVisibilityFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadQueries = useCallback(async () => {
    try {
      setLoading(true);
      const params: TelegramQueryListParams = {
        page: currentPage,
        limit: PAGE_SIZE,
        search: searchTerm,
      };
      if (filterType === 'public') params.isPublic = true;
      if (filterType === 'private') params.isPublic = false;

      const res = await api.telegramBI.queries.getAll(params);
      const data = payloadOf<TelegramQueryListPage>(res.data);
      setQueries(data?.queries || []);
      const total = data?.total || data?.totalCount || 0;
      setTotalCount(total);
      setTotalPages(Math.ceil(total / PAGE_SIZE) || 1);
    } catch {
      toast.error('Failed to load queries', { toastId: QUERIES_LOAD_TOAST_ID });
      setQueries([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterType]);

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const selectQuery = async (query: TelegramQueryRow) => {
    try {
      const res = await api.telegramBI.queries.get(query.query_id);
      setSelectedQuery(payloadOf<TelegramQueryRow>(res.data) || query);
    } catch {
      setSelectedQuery(query);
    }
    setQueryResults(null);
  };

  const executeQuery = async (query: TelegramQueryRow) => {
    try {
      setExecuting(true);
      if (!selectedQuery || selectedQuery.query_id !== query.query_id) {
        setSelectedQuery(query);
      }
      const res = await api.telegramBI.queries.execute(query.query_id);
      const d = payloadOf<TelegramQueryExecution>(res.data);
      const rows = d?.data || d?.results || [];
      const rowCount = d?.rowCount || rows.length;
      setQueryResults({
        data: rows,
        meta: { rowCount, executedAt: d?.executedAt, executionTime: d?.executionTimeMs },
      });
      toast.success(`${rowCount.toLocaleString()} rows returned`);
    } catch (err) {
      toast.error(apiError(err)?.error || apiError(err)?.message || 'Execution failed');
      setQueryResults(null);
    } finally {
      setExecuting(false);
    }
  };

  const createQuery = async (data: TelegramQueryForm) => {
    await api.telegramBI.queries.create(data);
    toast.success('Query created');
    setShowCreateModal(false);
    loadQueries();
  };

  const updateQuery = async (data: TelegramQueryForm) => {
    if (!selectedQuery) return;
    const res = await api.telegramBI.queries.update(selectedQuery.query_id, data);
    toast.success('Query updated');
    setShowEditModal(false);
    setSelectedQuery(payloadOf<TelegramQueryRow>(res.data) || selectedQuery);
    loadQueries();
  };

  const deleteQuery = async (id: number) => {
    if (!window.confirm('Delete this query? This cannot be undone.')) return;
    try {
      await api.telegramBI.queries.delete(id);
      toast.success('Query deleted');
      if (selectedQuery?.query_id === id) {
        setSelectedQuery(null);
        setQueryResults(null);
      }
      loadQueries();
    } catch (err) {
      toast.error(apiError(err)?.message || 'Failed to delete');
    }
  };

  const duplicateQuery = async (query: TelegramQueryRow) => {
    try {
      await api.telegramBI.queries.duplicate(query.query_id, `${query.query_name} (Copy)`);
      toast.success('Query duplicated');
      loadQueries();
    } catch (err) {
      toast.error(apiError(err)?.message || 'Failed to duplicate');
    }
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return formatRelativeTime(d) || '—';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaDatabase}
        title="Query Builder"
        subtitle="Create and execute SQL queries for Telegram reports"
        actions={
          <>
            {showAiAssist && (
              <button
                type="button"
                onClick={() => setShowAiModal(true)}
                aria-label="AI Assist"
                title="AI Assist"
                className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-lg transition-all shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                <FaRobot className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="page-header__label">AI Assist</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              aria-label="New Query"
              title="New Query"
              className="inline-flex items-center justify-center gap-2 h-10 px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <FaPlus className="w-3.5 h-3.5" aria-hidden="true" />
              <span>
                New<span className="page-header__label"> Query</span>
              </span>
            </button>
          </>
        }
      />

      <div className="p-3 sm:p-6 max-w-[1800px] mx-auto">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search queries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
            />
          </div>
          <ThemedSelect
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as TelegramVisibilityFilter)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
          >
            <option value="all">All Queries</option>
            <option value="public">Public</option>
            <option value="private">Private</option>
          </ThemedSelect>
          <span className="text-xs text-gray-400">{totalCount} total</span>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
          {/* Query List - 2 columns */}
          <div
            className="xl:col-span-2 bg-surface rounded-lg border border-gray-200 flex flex-col"
            style={{ maxHeight: 'calc(100vh - 240px)' }}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaDatabase className="text-primary-500 text-xs" />
                Queries
              </h2>
              <span className="text-xs text-gray-400">{queries.length} shown</span>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : queries.length === 0 ? (
                <div className="p-8 text-center">
                  <FaDatabase className="mx-auto text-2xl text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No queries found</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create your first query
                  </button>
                </div>
              ) : (
                queries.map((query) => (
                  <div
                    key={query.query_id}
                    onClick={() => selectQuery(query)}
                    className={`px-4 py-3 cursor-pointer transition-all hover:bg-gray-50/70 ${
                      selectedQuery?.query_id === query.query_id
                        ? 'bg-primary-50/50 border-l-2 border-primary-500'
                        : 'border-l-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {query.query_name}
                        </div>
                        {query.query_description && (
                          <div className="text-xs text-gray-500 truncate mt-0.5">
                            {query.query_description}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-1.5">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              query.is_public
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {query.is_public ? 'Public' : 'Private'}
                          </span>
                          <span className="text-[11px] text-gray-400">
                            {formatDate(query.updated_at || query.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            executeQuery(query);
                          }}
                          disabled={executing}
                          className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-40 transition-colors"
                          title="Execute"
                        >
                          <FaPlay className="text-[10px]" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectQuery(query).then(() => setShowEditModal(true));
                          }}
                          className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <FaEdit className="text-[10px]" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateQuery(query);
                          }}
                          className="p-1.5 rounded-md text-violet-600 hover:bg-violet-50 transition-colors"
                          title="Duplicate"
                        >
                          <FaCopy className="text-[10px]" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteQuery(query.query_id);
                          }}
                          className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <FaTrash className="text-[10px]" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
                >
                  <FaChevronLeft className="text-xs" />
                </button>
                <span className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
                >
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </div>

          {/* Query Detail + Results - 3 columns */}
          <div className="xl:col-span-3 space-y-4">
            {selectedQuery ? (
              <>
                {/* Query Header */}
                <div className="bg-surface rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 truncate">
                        {selectedQuery.query_name}
                      </h2>
                      {selectedQuery.query_description && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {selectedQuery.query_description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setShowHistory(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <FaHistory className="text-[10px]" />
                        History
                      </button>
                      <button
                        onClick={() => executeQuery(selectedQuery)}
                        disabled={executing}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg disabled:opacity-50 transition-colors"
                      >
                        {executing ? (
                          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <FaPlay className="text-[9px]" />
                        )}
                        {executing ? 'Running...' : 'Execute'}
                      </button>
                    </div>
                  </div>

                  {/* Inline SQL Editor */}
                  <div className="p-0">
                    <QueryEditor
                      query={selectedQuery}
                      readOnly={true}
                      onExecute={() => executeQuery(selectedQuery)}
                    />
                  </div>

                  {/* Query Meta */}
                  <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-100 bg-gray-50/50">
                    {[
                      {
                        label: 'Timeout',
                        value: `${selectedQuery.execution_timeout_seconds || 300}s`,
                      },
                      {
                        label: 'Max Rows',
                        value: (selectedQuery.max_rows || 10000).toLocaleString(),
                      },
                      { label: 'Source', value: selectedQuery.source_name || 'Default' },
                      { label: 'Version', value: `v${selectedQuery.version || 1}` },
                    ].map(({ label, value }) => (
                      <div key={label} className="text-[11px]">
                        <span className="text-gray-400">{label}: </span>
                        <span className="text-gray-600 font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Results */}
                {queryResults && <QueryResults results={queryResults} />}
              </>
            ) : (
              <div className="bg-surface rounded-lg border border-gray-200 p-12 text-center">
                <FaEye className="mx-auto text-3xl text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">Select a query to view details and execute</p>
                <p className="text-xs text-gray-400 mt-1">Or create a new query to get started</p>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {showCreateModal && (
          <CreateQueryModal onClose={() => setShowCreateModal(false)} onSubmit={createQuery} />
        )}
        {showEditModal && selectedQuery && (
          <EditQueryModal
            query={selectedQuery}
            onClose={() => setShowEditModal(false)}
            onSubmit={updateQuery}
          />
        )}
        {showHistory && selectedQuery && (
          <QueryHistory queryId={selectedQuery.query_id} onClose={() => setShowHistory(false)} />
        )}
        {showAiModal && (
          <AiAssistModal
            isOpen={true}
            onClose={() => setShowAiModal(false)}
            outputType="telegram"
            onCreated={() => loadQueries()}
          />
        )}
      </div>
    </div>
  );
};

export default QueryBuilder;
